import { useCustomMutation } from '@refinedev/core';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/copy-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api-client';
import type { RevealCredentialsResponse } from '@/lib/types';

const AUTO_CLEAR_SEC = 90;

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RevealDialog({ accountId, open, onOpenChange }: Props) {
  const { mutate, isLoading } = useCustomMutation<RevealCredentialsResponse>();

  const [reason, setReason] = useState('');
  const [revealed, setRevealed] = useState<RevealCredentialsResponse['data'] | null>(null);
  // Default EM CLARO (pedido do Lucas 16/08): quem revelou é porque quer ler;
  // o painel inteiro já fica atrás de login. O toggle continua pra ocultar.
  const [showSecrets, setShowSecrets] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLEAR_SEC);

  // Auto-clear timer
  useEffect(() => {
    if (!revealed) return;
    setSecondsLeft(AUTO_CLEAR_SEC);
    const interval = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          setRevealed(null);
          setShowSecrets(false);
          toast.info('Credenciais ocultadas automaticamente.');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [revealed]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setReason('');
      setRevealed(null);
      setShowSecrets(true);
      setSecondsLeft(AUTO_CLEAR_SEC);
    }
  }, [open]);

  const onReveal = () => {
    if (reason.trim().length < 10) {
      toast.error('Motivo precisa ter pelo menos 10 caracteres.');
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/reveal`,
        method: 'post',
        values: { reason: reason.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (result) => {
          setRevealed((result.data as unknown as RevealCredentialsResponse).data);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.errorCode === 'rate_limited') {
            const retry = (err.body as { retry_after_sec?: number })?.retry_after_sec ?? 0;
            const min = Math.ceil(retry / 60);
            toast.error(`Limite atingido. Tente novamente em ~${min} min.`);
          } else {
            toast.error('Falha ao revelar credenciais.');
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Revelar credenciais</DialogTitle>
          <DialogDescription>
            Ação registrada no audit log central com seu nome, IP e motivo. LGPD-sensitive.
          </DialogDescription>
        </DialogHeader>

        {!revealed ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Motivo (mín. 10 caracteres) *</Label>
              <Textarea
                id="reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: cliente fulano abriu chamado #1234 e precisa do código de recuperação"
              />
              <p className="text-xs text-muted-foreground">{reason.trim().length} / 10 chars</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={onReveal} disabled={isLoading || reason.trim().length < 10}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Revelar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                Limpar automaticamente em <span className="font-mono text-foreground">{secondsLeft}s</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSecrets((s) => !s)}
              >
                {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showSecrets ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>

            <FieldRow label="Email" value={revealed.email} mask={false} />
            <FieldRow
              label="Senha"
              value={revealed.password ?? '(não definida)'}
              mask={!showSecrets}
            />
            {revealed.totp_secret && (
              <FieldRow label="TOTP Secret" value={revealed.totp_secret} mask={!showSecrets} />
            )}
            {revealed.phone && (
              <FieldRow label="Telefone" value={revealed.phone} mask={!showSecrets} />
            )}
            <p className="text-xs text-muted-foreground">
              Restantes nesta hora: {revealed.rate_limit_remaining}
            </p>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, value, mask }: { label: string; value: string; mask: boolean }) {
  const display = mask ? '•'.repeat(Math.min(value.length, 16)) : value;
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1 rounded-md border bg-background px-3 py-2">
        <span className="flex-1 truncate font-mono text-sm">{display}</span>
        {!mask && <CopyButton value={value} label={label} />}
      </div>
    </div>
  );
}
